/**
 * ブラウザ情報を収集してログイン履歴用のデータを作成する
 */

export interface LoginInfo {
    userAgent: string;
    appType: string;
    referrer: string;
}

/**
 * PWAとして起動されているかを判定する
 */
const isPWA = (): boolean => {
    return window.matchMedia('(display-mode: standalone)').matches;
};

/**
 * アプリタイプを判定する
 */
export const getAppType = (): string => {
    if (isPWA()) {
        return 'pwa';
    }
    return 'web';
};

/**
 * トップページでのリファラー情報を取得する（外部サイトからの流入のみ）
 */
export const getTopPageReferrer = (): string => {
    // 1) URLクエリ ?from= を最優先で参照（SPA内遷移でも正確に取得するため）
    try {
        const currentUrl = new URL(window.location.href);
        const fromParam = currentUrl.searchParams.get('from');
        if (fromParam && fromParam.trim() !== '') {
            // 絶対URLならそのまま、相対パスなら現在オリジンを付与
            if (/^https?:\/\//i.test(fromParam)) {
                return fromParam;
            }
            const origin = `${currentUrl.protocol}//${currentUrl.host}`;
            const path = fromParam.startsWith('/') ? fromParam : `/${fromParam}`;
            return origin + path;
        }
    } catch (_) {
        // 何もしない（後続のdocument.referrerへフォールバック）
    }

    // document.referrerが存在し、空でない場合
    if (document.referrer && document.referrer.trim() !== '') {
        try {
            const referrerUrl = new URL(document.referrer);
            const currentUrl = new URL(window.location.href);

            // 同一ドメインからの遷移の場合
            if (referrerUrl.hostname === currentUrl.hostname) {
                // トップページ（/）からの遷移の場合は空文字を返す
                if (referrerUrl.pathname === '/') {
                    return '';
                }
                // トップページ以外からの遷移の場合はリファラーを返す
                return document.referrer;
            }

            // 外部ドメインからの遷移の場合のみリファラーを返す
            return document.referrer;
        } catch (error) {
            // URL解析に失敗した場合は空文字を返す
            return '';
        }
    }

    // リファラーが存在しない場合は空文字を返す
    return '';
};

/**
 * トップページでリファラー情報をセッションストレージに保存する
 */
export const saveTopPageReferrer = (): void => {
    const referrer = getTopPageReferrer();
    sessionStorage.setItem('topPageReferrer', referrer);
};

/**
 * セッションストレージからトップページのリファラー情報を取得する
 */
export const getStoredTopPageReferrer = (): string => {
    return sessionStorage.getItem('topPageReferrer') || '';
};

/**
 * ログイン情報を収集する（保存されたトップページのリファラーを使用）
 */
export const collectLoginInfo = (): LoginInfo => {
    return {
        userAgent: navigator.userAgent,
        appType: getAppType(),
        referrer: getStoredTopPageReferrer(),
    };
};

/**
 * ログイン情報をローカルストレージに保存する
 */
export const saveLoginInfoToStorage = (): void => {
    // もしすでに存在していたら、先に削除する
    getAndClearLoginInfoFromStorage();
    // 新しいログイン情報を保存する
    const loginInfo = collectLoginInfo();
    localStorage.setItem('tempLoginInfo', JSON.stringify(loginInfo));
};

/**
 * ローカルストレージからログイン情報を取得して削除する
 */
export const getAndClearLoginInfoFromStorage = (): LoginInfo | null => {
    const stored = localStorage.getItem('tempLoginInfo');
    if (stored) {
        localStorage.removeItem('tempLoginInfo');
        return JSON.parse(stored);
    }
    return null;
};
